import {GraphNodeDifferences} from "#graph/diffing/graph-node-differences"
import {GraphNodeSnapshot} from "#graph/diffing/graph-node-snapshot"
import type {ParameterDifferences} from "#graph/diffing/types"
import type {Evaluator} from "#graph/evaluators/types"
import {getGraphNodesByInstanceIds} from "#graph/helpers/collect"
import {graphParametersDifferences} from "#graph/helpers/diffing"
import {jsonSchema} from "#graph/helpers/json"
import {mapGraphParameters, traverseGraphParameters} from "#graph/helpers/parameters"
import {recursivelyReplaceSubObjects} from "#graph/helpers/replace"
import {BaseGraphNode} from "#graph/nodes/base/base-graph-node"
import type {NodeParameters} from "#graph/nodes/base/types"
import {CircularRef} from "#graph/nodes/circular-ref/circular-ref-node"
import type {GraphNodeDefinition} from "#graph/nodes/graph/types"
import {deserializeGraphNode} from "#graph/serialization/deserialize"
import {serializeGraphNode} from "#graph/serialization/serialize"
import type {SerializedGraphNode} from "#graph/serialization/types"
import {hashObject} from "@cm/utils"

export abstract class GraphNode<
    out Returns = unknown,
    in Context = unknown,
    out Parameters extends NodeParameters = NodeParameters,
    out ConstructorParameters extends NodeParameters = Parameters,
> extends BaseGraphNode<Parameters, ConstructorParameters> {
    public children: Set<GraphNode<unknown, Context>> = new Set()
    public parents: Set<GraphNode<unknown, Context>> = new Set()
    public abstract override get definition(): GraphNodeDefinition<Returns, Parameters, ConstructorParameters>

    public constructor(options: {parameters: ConstructorParameters; disableValidation?: false} | {parameters: Parameters; disableValidation: true}) {
        super(options)
        this.updateChildren(this.parameters)
    }

    updateChildren(parameters?: ConstructorParameters | Parameters) {
        this.children = new Set()
        this.parameters = traverseGraphParameters<GraphNode<unknown, Context>>(
            parameters ?? this.parameters,
            (value) => value instanceof GraphNode,
            (node) => {
                if (node instanceof GraphNode) {
                    this.children.add(node)
                    node.parents.add(this)
                }
            },
        ) as Parameters
    }

    run = (_evaluator: Evaluator<Context>): Promise<Returns> => {
        throw new Error(`Method run is not implemented for ${this.nodeClass}`)
    }
    runSync = (_evaluator: Evaluator<Context>): Returns => {
        throw new Error(`Method runSync is not implemented for ${this.nodeClass}`)
    }

    get returnsSchema() {
        return this.definition.validation.returns
    }

    override replaceParameters(newParameters: Parameters, disableValidation: true): void
    override replaceParameters(newParameters: ConstructorParameters, disableValidation?: false): void
    override replaceParameters(newParameters: ConstructorParameters | Parameters, disableValidation = false): void {
        const parameters = (disableValidation ? newParameters : this.ensureValidParameters(newParameters as ConstructorParameters)) as Parameters
        traverseGraphParameters<GraphNode<unknown, Context>>(
            this.parameters,
            (value) => value instanceof BaseGraphNode,
            (node) => {
                if (node instanceof GraphNode) {
                    this.children.delete(node)
                    node.parents.delete(this)
                }
            },
        )
        this.parameters = traverseGraphParameters<GraphNode<unknown, Context>>(
            parameters,
            (value) => value instanceof BaseGraphNode,
            (node) => {
                if (node instanceof GraphNode) {
                    this.children.add(node)
                    node.parents.add(this)
                }
            },
        ) as Parameters
    }

    serialize() {
        const serializedGraph = serializeGraphNode(this)
        return jsonSchema.parse(serializedGraph) as SerializedGraphNode
    }

    //For illustration of orders, see: https://en.wikipedia.org/wiki/Tree_traversal
    private _iterate = (depthFirst: boolean = false): Iterator<GraphNode<unknown, Context>> => {
        const visitedNodes = new Set<GraphNode<unknown, Context>>()
        let nodesToVisit: GraphNode<unknown, Context>[] = [this]
        const visitNode = () => {
            const firstNode = nodesToVisit.pop()
            if (firstNode) {
                visitedNodes.add(firstNode)
                nodesToVisit = (depthFirst ? [...firstNode.children, ...nodesToVisit] : [...nodesToVisit, ...firstNode.children]).filter(
                    (node) => !visitedNodes.has(node),
                )
            }
            return firstNode
        }
        return {
            next: () => {
                const node = visitNode()
                if (node) {
                    return {value: node, done: false}
                } else {
                    return {value: undefined, done: true}
                }
            },
        }
    }

    public [Symbol.iterator] = () => this._iterate(true)

    clone(options?: {
        cloneSubNode?: (parent: BaseGraphNode, subNode: BaseGraphNode, path: string) => boolean
        parameterOverrides?: Partial<Parameters> | ((node: BaseGraphNode) => NodeParameters | undefined)
        cloneInstanceIds?: boolean
        beforeReplaceClonedSubNodes?: (clonedNode: BaseGraphNode) => void
    }): this {
        const cloneSubNodes = options?.cloneSubNode ?? (() => false)
        const parameterOverrides = options?.parameterOverrides
        const parameterOverridesFn =
            typeof parameterOverrides === "function"
                ? parameterOverrides
                : (node: BaseGraphNode) => {
                      if (node === this) return parameterOverrides
                      return undefined
                  }
        const cloneInstanceIds = options?.cloneInstanceIds ?? false

        const replacedNodes = new Map<BaseGraphNode, BaseGraphNode>()
        const clonedChildPaths = new Map<BaseGraphNode, Set<string>>()
        function cloneImpl(node: BaseGraphNode): BaseGraphNode {
            const cachedRefId = replacedNodes.get(node)
            if (cachedRefId !== undefined) return cachedRefId

            const cycleRef = new CircularRef({parameters: {refId: replacedNodes.size}})
            replacedNodes.set(node, cycleRef)

            const childPaths = new Set<string>()
            const parameterOverrides = parameterOverridesFn(node)
            const retVal = new (node.constructor as {
                new (options: {parameters: NodeParameters; disableValidation: boolean}): BaseGraphNode
            })({
                parameters: {
                    ...traverseGraphParameters<BaseGraphNode>(
                        node.parameters,
                        (value) => value instanceof BaseGraphNode,
                        (child, path) => {
                            if ((!parameterOverrides || !(path in parameterOverrides)) && cloneSubNodes(node, child, path)) {
                                childPaths.add(path)
                                return cloneImpl(child)
                            } else return child
                        },
                    ),
                    ...parameterOverrides,
                },
                disableValidation: true,
            })
            if (cloneInstanceIds) retVal.instanceId = node.instanceId

            replacedNodes.set(node, retVal)
            replacedNodes.set(cycleRef, retVal)
            clonedChildPaths.set(retVal, childPaths)

            return retVal
        }

        const clonedNode = cloneImpl(this) as this

        const visitedNodes = new Set<BaseGraphNode>()
        function replaceClonedSubNodes(node: BaseGraphNode) {
            const childPaths = clonedChildPaths.get(node)
            if (!childPaths) return

            const newParameters = traverseGraphParameters<BaseGraphNode>(
                node.parameters,
                (value) => value instanceof BaseGraphNode,
                (child, path) => {
                    if (!childPaths.has(path)) return child

                    const retValue = replacedNodes.get(child) ?? child

                    if (visitedNodes.has(retValue)) return retValue
                    visitedNodes.add(retValue)

                    replaceClonedSubNodes(retValue)
                    return retValue
                },
            )
            node.replaceParameters(newParameters)
        }
        options?.beforeReplaceClonedSubNodes?.(clonedNode)
        replaceClonedSubNodes(clonedNode)

        return clonedNode
    }

    getSnapshot(): GraphNodeSnapshot<BaseGraphNode, GraphNode<unknown, Context>> {
        return new GraphNodeSnapshot<BaseGraphNode, GraphNode<unknown, Context>>(this)
    }

    trackDifferences(modify: (graph: this) => void, keepChanges = true): GraphNodeDifferences<GraphNode<unknown, Context>> {
        const snapshot = this.getSnapshot()
        modify(this)
        const differences = this.getDifferences(snapshot)
        if (!keepChanges) differences.applyBackward()
        return differences
    }

    getDifferences(oldSnapshot: GraphNodeSnapshot<BaseGraphNode, GraphNode<unknown, Context>>): GraphNodeDifferences<GraphNode<unknown, Context>> {
        const {originalNodes, oldClonedNodes, node} = oldSnapshot
        if (node !== this) throw new Error("NodeGraphSnapshot does not match this node")

        const newNodes = getGraphNodesByInstanceIds<GraphNode<unknown, Context>>(this)

        const allNodes = new Map<string, GraphNode<unknown, Context>>([...originalNodes, ...newNodes])

        const addedNodes = new Set<GraphNode<unknown, Context>>()
        const modifiedNodes = new Map<GraphNode<unknown, Context>, ParameterDifferences>()
        const deletedNodes = new Set<GraphNode<unknown, Context>>()
        for (const newNode of allNodes.values()) {
            if (!newNodes.has(newNode.instanceId)) deletedNodes.add(newNode)

            const preChangeNodeClone = oldClonedNodes.get(newNode.instanceId)
            if (!preChangeNodeClone) {
                addedNodes.add(newNode)
            } else {
                const preChangedNodeParameters = traverseGraphParameters<BaseGraphNode>(
                    preChangeNodeClone.parameters,
                    (value) => value instanceof BaseGraphNode,
                    (node) => {
                        const originalNode = originalNodes.get(node.instanceId)
                        if (!originalNode) throw new Error(`Original node ${node.instanceId} not found in graph`)
                        return originalNode
                    },
                )
                const modifiedParameters = graphParametersDifferences(preChangedNodeParameters, newNode.parameters, true)
                if (modifiedParameters.length > 0) modifiedNodes.set(newNode, modifiedParameters)
            }
        }

        return new GraphNodeDifferences<GraphNode<unknown, Context>>(this, addedNodes, modifiedNodes, deletedNodes)
    }

    getHash() {
        type HashableObject = {
            $class: string
            $parameters: NodeParameters
            $version: number
        }

        const resolvedHashableObjects = new Map<CircularRef, HashableObject>()

        const getHashableObject = (node: BaseGraphNode, refCache: Map<BaseGraphNode, HashableObject | CircularRef>): HashableObject | CircularRef => {
            const cachedHashableObject = refCache.get(node)
            if (cachedHashableObject) return cachedHashableObject

            const circularRef = new CircularRef({parameters: {refId: refCache.size}})
            refCache.set(node, circularRef)

            const hashableObject: HashableObject = {
                $class: node.nodeClass,
                $parameters: mapGraphParameters<BaseGraphNode, CircularRef | HashableObject>(
                    node.parameters,
                    (value) => value instanceof BaseGraphNode,
                    (child) => getHashableObject(child, refCache),
                ),
                $version: node.nodeVersion,
            }

            refCache.set(node, hashableObject)
            resolvedHashableObjects.set(circularRef, hashableObject)

            return hashableObject
        }

        const hashableObject = getHashableObject(this, new Map()) as HashableObject

        const objectMapper = (reference: object): object | void => {
            if (reference instanceof CircularRef) {
                const resolvedHashObject = resolvedHashableObjects.get(reference)
                if (!resolvedHashObject) {
                    throw new Error(`Circular reference ${reference.parameters.refId} not found in resolved hash objects`)
                }
                return resolvedHashObject
            }
        }

        recursivelyReplaceSubObjects(hashableObject, objectMapper)
        return hashObject(hashableObject)
    }

    public static deserialize<OwnType, Parameters extends NodeParameters = NodeParameters>(
        this: {new (parameters: Parameters, disableValidation?: boolean): OwnType},
        data: SerializedGraphNode,
        namespace?: string,
    ): OwnType {
        const result = deserializeGraphNode(data, namespace)
        if (result instanceof this) {
            return result
        }
        // nodeClass is a static property, but we cannot declare it as both static and abstract, so this cast is needed
        throw new Error(`Cannot deserialize node of class ${result.nodeClass} as ${this.prototype.definition.nodeClass}`)
    }
}
